import { type RouteObject } from 'react-router'

import { lazyComponent } from 'routes/lazy-route'
import {
  DASHBOARDS_2_1_DETAIL_TRANSACTION_PATH,
  DASHBOARDS_2_1_DETAIL_URL,
  DASHBOARDS_2_1_TEMPLATE_SETUP_TRANSACTION_PATH,
  DASHBOARDS_2_1_TRANSACTION_PATH,
  DASHBOARDS_2_1_URL,
  SHARE_DASHBOARD_V2_1,
} from 'src/features/dashboards-v2-1/paths'

/** Warms the edit page's lazy chunk ahead of navigation — call this
 *  fire-and-forget right when a "create dashboard" action starts, in
 *  parallel with the network request that creates the dashboard, so the
 *  chunk fetch (which currently happens only after navigate() runs) isn't a
 *  second, sequential wait on top of the create request. Kept beside the
 *  route's own `lazy` import below so the two specifiers can't drift apart. */
export const prefetchDashboards21EditPage = (): void => {
  void import('src/features/dashboards-v2-1/features/edit')
}

export const sharedDashboardsV21Routes: RouteObject[] = [
  {
    path: SHARE_DASHBOARD_V2_1,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2-1/features/shared'),
      'DashboardSharePage',
    ),
  },
]

export const dashboardsV21Routes: RouteObject[] = [
  {
    path: DASHBOARDS_2_1_URL,
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2-1/features/list-v2'),
      'Dashboards2ListPage',
    ),
    handle: {
      featureKey: DASHBOARDS_2_1_URL,
      sentryTransactionPath: DASHBOARDS_2_1_TRANSACTION_PATH,
    },
  },
  {
    path: `${DASHBOARDS_2_1_URL}/templates/:templateId/setup`,
    lazy: lazyComponent(
      () =>
        import('src/features/dashboards-v2-1/features/templates/features/setup'),
      'DashboardTemplateSetupPage',
    ),
    handle: {
      // Piggy-back on the list feature key so users with dashboards access
      // reach setup without a separate access rule.
      featureKey: DASHBOARDS_2_1_URL,
      sentryTransactionPath: DASHBOARDS_2_1_TEMPLATE_SETUP_TRANSACTION_PATH,
    },
  },
  {
    path: `${DASHBOARDS_2_1_URL}/:customDashboardId`,
    // One role-gated surface for every authenticated user (owner or shared
    // viewer) — the former separate view/edit routes are merged; `edit/`
    // computes `canEdit` from ownership and gates affordances internally.
    lazy: lazyComponent(
      () => import('src/features/dashboards-v2-1/features/edit'),
      'DashboardEditPage',
    ),
    handle: {
      featureKey: DASHBOARDS_2_1_DETAIL_URL,
      sentryTransactionPath: DASHBOARDS_2_1_DETAIL_TRANSACTION_PATH,
    },
  },
]
